import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useDebounce } from '@namespace/helpers';
import { Box } from '../Box';
import { Input } from '../Input';
import { Select } from '../Select';
import { Option } from '../Select/types';

import styles from './index.module.css';

interface Value {
  value: string;
  title: string;
}

interface CustomTriggerProps {
  inputValue: string;
  onChange: (e: ChangeEvent<HTMLInputElement>, isOpen: boolean) => void;
  isOpen: boolean;
}

interface InputSelectProps {
  size?: 's' | 'm';
  name?: string;
  value: Value;
  placeholder?: string;
  onChange?: (selected: Value) => void;
  loadAsyncOptions?: (input: string) => Promise<Value[]>;
  disabled?: boolean;
}

const CustomTrigger: FC<CustomTriggerProps> = ({
  inputValue,
  onChange,
  isOpen,
  ...props
}) => (
  <Input
    {...props}
    value={inputValue}
    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e, isOpen)}
  />
);

export const InputSelect: FC<InputSelectProps> = ({
  size = 's',
  name,
  value = { value: '', title: '' },
  placeholder,
  onChange = () => {},
  loadAsyncOptions,
  disabled,
  ...props
}) => {
  const { title: valueTitle = '' } = value;
  const [inputValue, changeInputValue] = useState<string>('');
  const debouncedInputValue = useDebounce(inputValue, 500);
  const [asyncOptions, setAsyncOptions] = useState<Value[]>([]);

  const filteredOptions = useMemo(
    () =>
      asyncOptions.filter(({ title }) =>
        title.toLowerCase().includes(debouncedInputValue.toLowerCase())
      ),
    [debouncedInputValue, asyncOptions]
  );

  const formattedOptions = useMemo(
    () =>
      filteredOptions.map(({ value: v, title }) => ({
        value: v,
        label: title
      })),
    [filteredOptions]
  );

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, isOpen: boolean) => {
      changeInputValue(e.target.value);
      if (!isOpen) {
        e.target.click?.();
      }
    },
    []
  );

  const onChangeSelect = useCallback(
    (selectedValue: string | number) => {
      const selected = filteredOptions.find(
        (o) => o.value === selectedValue
      ) || {
        value: '',
        title: ''
      };

      onChange(selected);
      changeInputValue(selected.title);
    },
    [filteredOptions, onChange]
  );

  useEffect(() => {
    if (loadAsyncOptions) {
      loadAsyncOptions(debouncedInputValue).then((list = []) =>
        setAsyncOptions(list)
      );
    }
  }, [loadAsyncOptions, debouncedInputValue]);

  useEffect(() => {
    if (valueTitle !== inputValue) {
      changeInputValue(valueTitle);
    }
  }, [inputValue, valueTitle]);

  useEffect(() => {
    if (!value.value && !value.title) {
      changeInputValue('');
      setAsyncOptions([]);
    }
  }, [value]);

  return (
    <Box direction="column" className={styles[size]}>
      <Select
        name={name}
        size={size}
        placeholder={placeholder}
        disabled={disabled}
        CustomTrigger={
          <CustomTrigger
            onChange={onInputChange}
            inputValue={inputValue}
            isOpen={false}
          />
        }
        {...props}
        onChange={onChangeSelect}
        options={formattedOptions as Option[]}
        isDesktop={true}
      />
    </Box>
  );
};
